"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { type CarouselApi } from "@/shadcn/ui/carousel";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import arrowRight from "@/public/lp2/component2/right.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function ScheduleSection({
  data,
}: {
  data: PageMentoringSchema;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const containerRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      tl.from(containerRef.current, {
        y: "1rem",
        delay: 0.25,
        scrollTrigger: {
          trigger: containerRef.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  const changeCurrent = useCallback(
    (index: number) => {
      if (!api) {
        return;
      }

      api.scrollTo(index);
    },
    [api]
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <section className="w-full">
      <div className="w-full max-w-[178rem] mx-auto pt-[4rem] lg:pt-[16rem] pb-[10rem]">
        <div
          ref={containerRef}
          className="flex flex-col items-start justify-center w-full"
        >
          {data?.scheduleTitle && (
            <PortableText
              value={data?.scheduleTitle ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="gradient-title text-[4rem] lg:text-[8rem] px-[4rem] lg:px-0 font-[500] tracking-[-0.29rem] leading-[110%] w-full mb-0 lg:mb-[6rem] text-center">
                      {children}
                    </h2>
                  ),
                },
              })}
            />
          )}

          {/* desktop */}
          <div className="hidden lg:flex w-full flex-col">
            {/* head */}
            <div
              className="w-full flex border border-[#80FFD980] bg-[#80FFD91A] rounded-[2rem] backdrop-blur-[7.5rem]"
              style={{
                boxShadow: "0 20.592px 45.07px 0 rgba(128, 255, 217, 0.15)",
              }}
            >
              <div className="lg:w-1/2 border-r border-white/10 p-[2rem] text-center">
                <span className="text-white text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[110%]">
                  WOCHE
                </span>
              </div>
              <div className="lg:w-full border-r border-white/10 p-[2rem] text-center">
                <span className="text-white text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[110%]">
                  MONTAG
                </span>
              </div>
              <div className="lg:w-1/2 border-r border-white/10 p-[2rem] text-center">
                <span className="text-white text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[110%]">
                  DIENSTAG
                </span>
              </div>
              <div className="lg:w-full border-r border-white/10 p-[2rem] text-center">
                <span className="text-white text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[110%]">
                  MITTWOCH
                </span>
              </div>
              <div className="lg:w-full border-r border-white/10 p-[2rem] text-center">
                <span className="text-white text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[110%]">
                  DONNERSTAG
                </span>
              </div>
              <div className="lg:w-1/2 p-[2rem] text-center">
                <span className="text-white text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[110%]">
                  FREITAG
                </span>
              </div>
            </div>
            {/* end head */}
            {/* body */}
            {data?.schedule?.map((line, index) => (
              <div
                key={index}
                className={cn(
                  "w-full flex rounded-[.5rem]",
                  index % 2 !== 0 && "bg-white/5"
                )}
              >
                {/* WOCHE */}
                <div className="lg:w-1/2 border-r border-white/10 p-[2rem] text-center">
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-[#1ECC97] text-[2.5rem] lg:text-[2.25rem] font-[600] lg:font-[500] leading-[140%]">
                      {index + 1}
                    </span>
                  </div>
                </div>

                {/* MONTAG */}
                <div className="lg:w-full border-r border-white/10 p-[2rem]">
                  {line?.monday &&
                    line?.monday?.length &&
                    line?.monday?.map((day, index) => (
                      <div
                        key={index + 100}
                        className="w-full flex items-start justify-between gap-[2rem]"
                      >
                        <div className="w-full flex items-center justify-start gap-[.25rem]">
                          <span
                            className="w-[1rem] h-[1rem] min-w-[1rem] min-h-[1rem] rounded-full"
                            style={{ backgroundColor: day.bulletColor }}
                          ></span>
                          <span className="text-white/80 text-[2.5rem] lg:text-[2rem] font-[500] leading-[140%]">
                            {day.time}
                          </span>
                        </div>
                        <span className="w-full text-white/70 text-[2.5rem] lg:text-[2rem] font-[300] leading-[140%] text-end">
                          {day.text}
                        </span>
                      </div>
                    ))}
                </div>
                {/* DIENSTAG */}
                <div className="lg:w-1/2 border-r border-white/10 p-[2rem] text-center flex items-center justify-center">
                  <span className="text-white/30 text-[2.5rem] lg:text-[2rem] font-[600] lg:font-[500] leading-[110%]">
                    -
                  </span>
                </div>
                {/* MITTWOCH */}
                <div className="lg:w-full border-r border-white/10 p-[2rem] text-center">
                  {line?.wednesday &&
                    line?.wednesday?.length &&
                    line?.wednesday?.map((item, index) => (
                      <div
                        key={index + 200}
                        className="w-full flex items-start justify-between gap-[2rem]"
                      >
                        <div className="w-full flex items-center justify-start gap-[.25rem]">
                          <span
                            className="w-[1rem] h-[1rem] min-w-[1rem] min-h-[1rem] rounded-full"
                            style={{
                              backgroundColor: item?.bulletColor,
                            }}
                          ></span>
                          <span className="text-white/80 text-[2.5rem] lg:text-[2rem] font-[500] leading-[140%]">
                            {item?.time}
                          </span>
                        </div>
                        <span className="w-full text-white/70 text-[2.5rem] lg:text-[2rem] font-[300] leading-[140%] text-end">
                          {item?.text}
                        </span>
                      </div>
                    ))}

                  {(!line?.wednesday || !line?.wednesday?.length) && (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-white/30 text-[2.5rem] lg:text-[2rem] font-[600] lg:font-[500] leading-[110%]">
                        -
                      </span>
                    </div>
                  )}
                </div>
                {/* DONNERSTAG */}
                <div className="lg:w-full border-r border-white/10 p-[2rem]">
                  {line?.thursday &&
                    line?.thursday?.length &&
                    line?.thursday?.map((item, index) => (
                      <div
                        key={index + 300}
                        className="w-full flex items-start justify-between gap-[2rem]"
                      >
                        <div className="w-full flex items-center justify-start gap-[.25rem]">
                          <span
                            className="w-[1rem] h-[1rem] min-w-[1rem] min-h-[1rem] rounded-full"
                            style={{
                              backgroundColor: item?.bulletColor,
                            }}
                          ></span>
                          <span className="text-white/80 text-[2.5rem] lg:text-[2rem] font-[500] leading-[140%]">
                            {item?.time}
                          </span>
                        </div>
                        <span className="w-full text-white/70 text-[2.5rem] lg:text-[2rem] font-[300] leading-[140%] text-end">
                          {item?.text}
                        </span>
                      </div>
                    ))}

                  {(!line?.thursday || !line?.thursday?.length) && (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-white/30 text-[2.5rem] lg:text-[2rem] font-[600] lg:font-[500] leading-[110%]">
                        -
                      </span>
                    </div>
                  )}
                </div>
                {/* FREITAG */}
                <div className="lg:w-1/2 p-[2rem] text-center">
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-white/30 text-[2.5rem] lg:text-[2rem] font-[600] lg:font-[500] leading-[110%]">
                      -
                    </span>
                  </div>
                </div>
                {/*  */}
              </div>
            ))}
            {/* end body */}
          </div>
          {/* end desktop */}

          {/* mobile */}
          <div className="w-full lg:hidden">
            <Carousel
              setApi={setApi}
              opts={{
                loop: true,
              }}
            >
              <CarouselContent className="w-full py-[4.5rem]">
                {data?.schedule?.map((line, index) => {
                  return (
                    <CarouselItem key={index + 100} className="basis-[85%]">
                      <div
                        key={index}
                        className="w-full h-full border border-[#80FFD9]/20 rounded-[2rem] backdrop-blur-[.125rem] p-[3rem] flex flex-col"
                        style={{
                          background:
                            "linear-gradient(0deg, rgba(128, 255, 217, 0.03) 0%, rgba(128, 255, 217, 0.03) 100%), linear-gradient(97deg, rgba(128, 255, 217, 0.00) -5.86%, rgba(128, 255, 217, 0.07) 97.96%)",
                        }}
                      >
                        {/* WOCHE */}
                        <div className="w-full mb-[2rem]">
                          <span className=" text-white/80 text-[3rem] font-[600] leading-[140%] text-end">
                            Woche {index + 1}
                          </span>
                        </div>
                        {/* MONTAG */}
                        <div className="w-full mb-[.5rem] border-b border-white/5 py-[.5rem]">
                          {line.monday.map((day, index) => (
                            <div
                              key={index + 300}
                              className="w-full flex justify-between gap-[2rem] mb-[.5rem]"
                            >
                              <span className="w-full text-white/80 text-[2.25rem] font-[600] leading-[140%]">
                                {index === 0 && "MONTAG"}
                              </span>
                              <div className="w-full flex flex-col">
                                <div className="w-full flex items-center gap-[.5rem]">
                                  <span
                                    className="w-[1rem] h-[1rem] min-w-[1rem] min-h-[1rem] rounded-full"
                                    style={{ backgroundColor: day.bulletColor }}
                                  ></span>
                                  <span className="text-white/90 text-[2.25rem] font-[500] leading-[140%]">
                                    {day.time}
                                  </span>
                                </div>
                                <span className="w-full text-white/70 text-[2.25rem] lg:text-[2rem] font-[300] leading-[140%] ps-[2rem]">
                                  {day.text}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* MITTWOCH */}
                        {line?.wednesday && line?.wednesday?.length && (
                          <div className="w-full mb-[.5rem] border-b border-white/5 py-[.5rem]">
                            <div className="w-full flex justify-between gap-[2rem] mb-[.5rem]">
                              <span className="w-full text-white/80 text-[2.25rem] font-[600] leading-[140%]">
                                MITTWOCH
                              </span>
                              <div className="w-full flex flex-col">
                                {line?.wednesday?.map((day, index) => (
                                  <div
                                    key={index + 400}
                                    className="w-full flex flex-col"
                                  >
                                    <div className="w-full flex items-center gap-[.5rem]">
                                      <span
                                        className="w-[1rem] h-[1rem] min-w-[1rem] min-h-[1rem] rounded-full"
                                        style={{
                                          backgroundColor: day.bulletColor,
                                        }}
                                      ></span>
                                      <span className="text-white/90 text-[2.25rem] font-[500] leading-[140%]">
                                        {day.time}
                                      </span>
                                    </div>
                                    <span className="w-full text-white/70 text-[2.25rem] lg:text-[2rem] font-[300] leading-[140%] ps-[2rem]">
                                      {day.text}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* DONNERSTAG */}
                        {line?.thursday && line?.thursday?.length && (
                          <div className="w-full mb-[.5rem] border-b border-white/5 py-[.5rem]">
                            <div className="w-full flex justify-between gap-[2rem] mb-[.5rem]">
                              <span className="w-full text-white/80 text-[2.25rem] font-[600] leading-[140%]">
                                DONNERSTAG
                              </span>

                              <div className="w-full flex flex-col gap-[2rem]">
                                {line?.thursday?.map((day, index) => (
                                  <div
                                    key={index + 400}
                                    className="w-full flex flex-col"
                                  >
                                    <div className="w-full flex items-center gap-[.5rem]">
                                      <span
                                        className="w-[1rem] h-[1rem] min-w-[1rem] min-h-[1rem] rounded-full"
                                        style={{
                                          backgroundColor: day.bulletColor,
                                        }}
                                      ></span>
                                      <span className="text-white/90 text-[2.25rem] font-[500] leading-[140%]">
                                        {day.time}
                                      </span>
                                    </div>
                                    <span className="w-full text-white/70 text-[2.25rem] lg:text-[2rem] font-[300] leading-[140%] ps-[2rem]">
                                      {day.text}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {/*  */}
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>
            {/* constrols */}
            <div className="w-full flex flex-col items-center justify-center gap-[8rem] px-4">
              <div className="w-full flex items-center justify-between gap-[4rem]">
                <button
                  onClick={() =>
                    changeCurrent(
                      current === 0 ? data?.schedule?.length - 1 : current - 1
                    )
                  }
                  className="mentoring-slider-indicator-gradient border border-[#bcc6ec80] min-w-[7rem] max-w-[7rem] min-h-[7rem] max-h-[7rem] rounded-full flex items-center justify-center"
                >
                  <Image
                    src={arrowRight}
                    alt="arrow left"
                    className="w-[3rem] h-[3rem] rotate-180"
                  />
                </button>

                <div className="w-full flex items-stretch justify-center gap-[1.6rem]">
                  {data?.schedule?.map((_, index) => {
                    return (
                      <button
                        key={index}
                        onClick={() => changeCurrent(index)}
                        className={`${
                          index === current ? "opacity-100" : "opacity-20"
                        } bg-[#1ECC97] w-full h-[1.5rem] rounded-full`}
                      ></button>
                    );
                  })}
                </div>

                <button
                  onClick={() =>
                    changeCurrent(
                      data?.schedule?.length === current + 1 ? 0 : current + 1
                    )
                  }
                  className="mentoring-slider-indicator-gradient border border-[#bcc6ec80] min-w-[7rem] max-w-[7rem] min-h-[7rem] max-h-[7rem] rounded-full flex items-center justify-center"
                >
                  <Image
                    src={arrowRight}
                    alt="arrow right"
                    className="w-[3rem] h-[3rem]"
                  />
                </button>
              </div>
            </div>
          </div>
          {/* end mobile */}
        </div>
      </div>
    </section>
  );
}
